import { QueryClient, QueryKey, useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { DATA_QUERY_KEY } from '@/core';
import { cancelProgressiveQuery, isProgressiveQuery, isProgressiveQueryFetchingMore } from './progressive-query-utils';

export const ALL_DATA_QUERIES_QUERY_KEY = [DATA_QUERY_KEY];

/**
 * Hook that returns a function that will cancel in progress data queries associated with the visualization framework.
 *
 * The hook takes one optional argument that is a `queryKey` filter. When no arguments are provided, all
 * active data queries will be cancelled. When a `queryKey` is provided, queries that match that queryKey will be
 * cancelled.
 *
 * See https://tanstack.com/query/latest/docs/framework/react/guides/filters for additional information about how
 * queryKey filters work.
 */
export function useCancelInProgressDataQueries(queryKey: QueryKey = ALL_DATA_QUERIES_QUERY_KEY) {
  const queryClient = useQueryClient();

  const isFetching = useIsFetching({
    queryKey: queryKey,
  });

  const cancelInProgressDataQueriesFn = useCallback(async () => {
    await cancelInProgressDataQueries(queryClient, queryKey);
  }, [queryClient, queryKey]);

  return {
    isFetching,
    cancelInProgressDataQueries: cancelInProgressDataQueriesFn,
  };
}

/**
 * Function that will cancel in progress data queries associated with the visualization framework.
 *
 * The function's second optional argument is a `queryKey` filter. When this argument is not provided, all
 * active data queries will be cancelled. When a `queryKey` is provided, queries that match that queryKey will be
 * cancelled.
 *
 * See https://tanstack.com/query/latest/docs/framework/react/guides/filters for additional information about how
 * queryKey filters work.
 */
export async function cancelInProgressDataQueries(
  queryClient: QueryClient,
  queryKey: QueryKey = ALL_DATA_QUERIES_QUERY_KEY
) {
  await queryClient.cancelQueries({
    queryKey: queryKey,
    predicate: (query) => {
      // Normal fetching status.
      const isFetching = query.state.fetchStatus === 'fetching';

      if (isProgressiveQuery(query)) {
        if (isFetching || isProgressiveQueryFetchingMore(query)) {
          cancelProgressiveQuery(queryClient, query);

          return true;
        }
      }

      // Basic queries can just return `true` here to cancel and react-query handles the rest
      // for us.
      return isFetching;
    },
  });
}
